import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FC } from 'react';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { useUnitPolicies } from '@/hooks/use-unit-policies';
import { composeTableOptions } from '@/table';
import { AlexaSkillPolicy } from '@/types/alexa-skill-policy';
import { cx } from '@/utils/cx';

const hasEnabledMismatch = (skill: AlexaSkillPolicy): boolean =>
  skill.actualEnabled !== undefined && skill.actualEnabled !== skill.enabled;

const hasStageMismatch = (skill: AlexaSkillPolicy): boolean =>
  skill.actualStage !== undefined && skill.actualStage !== skill.stage;

const columns: MRT_ColumnDef<AlexaSkillPolicy>[] = [
  {
    accessorKey: 'skillName',
    header: 'Skill Name',
    grow: true,
    size: 200,
  },
  {
    accessorKey: 'skillId',
    header: 'Skill ID',
    size: 250,
  },
  {
    id: 'status',
    header: 'Status',
    size: 300,
    Cell: ({ row }) => {
      const { enabled, stage, actualEnabled, actualStage } = row.original;
      const enabledMismatch = hasEnabledMismatch(row.original);
      const stageMismatch = hasStageMismatch(row.original);

      const displayEnabled = actualEnabled ?? enabled;
      const displayStage = actualStage ?? stage;

      return (
        <div className="flex items-center gap-2">
          <div
            className={cx(
              'flex items-center gap-1 rounded px-2 py-1',
              enabledMismatch && 'bg-warningTint',
            )}
          >
            <StatusBadge
              text={displayEnabled ? 'Enabled' : 'Disabled'}
              variant={
                enabledMismatch
                  ? 'warning'
                  : displayEnabled
                    ? 'success'
                    : 'alert'
              }
            />
            {enabledMismatch && (
              <span className="text-xs text-warningShade">
                (expected: {enabled ? 'enabled' : 'disabled'})
              </span>
            )}
          </div>
          <div
            className={cx(
              'flex items-center gap-1 rounded px-2 py-1',
              stageMismatch && 'bg-warningTint',
            )}
          >
            <span
              className={cx(
                'text-xs',
                stageMismatch
                  ? 'font-medium text-warningShade'
                  : 'text-gray-500',
              )}
            >
              {displayStage}
            </span>
            {stageMismatch && (
              <span className="text-xs text-warningShade">
                (expected: {stage})
              </span>
            )}
          </div>
        </div>
      );
    },
  },
];

export const DeviceFunctionsView: FC = () => {
  const { data: policies, isLoading: isLoadingPolicies } = useUnitPolicies();

  const alexaSkills = policies?.alexaSkills ?? [];

  const table = useMaterialReactTable({
    ...composeTableOptions<AlexaSkillPolicy>({ enableRowActions: false }),
    columns,
    enableColumnResizing: true,
    data: alexaSkills,
    state: {
      isLoading: isLoadingPolicies,
      ...(isLoadingPolicies
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
  });

  return (
    <div>
      <SectionHeadingGroup
        className="mb-6"
        subtitle="Skill configurations for Alexa-enabled devices in this unit."
        title="Device Functions"
      />
      <MaterialReactTable table={table} />
    </div>
  );
};
